import { Button, Link, SearchField, Text } from '@shared/components';
import {
  GetExistingCarriersQuery,
  useGetExistingCarriersQuery,
} from '@shared/generated/graphql';
import { useEffect, useRef, useState } from 'react';
import { PiCheck, PiPlus, PiTruck } from 'react-icons/pi';
import { CarrierContactsList } from './CarrierContactsList';

export type ExistingCarrierNode = NonNullable<
  GetExistingCarriersQuery['getExistingCarriers']['nodes']
>[number];

export type OutboundPlanCarrier = {
  carrierId: string;
  carrierName: string;
  mcNumber?: string | null;
  contacts?: {
    id: string;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  }[];
};

type ExistingCarriersProps = {
  shipmentId: string;
  pageSize?: number;
  onSelectCarrier?: (carrier: ExistingCarrierNode) => void;
  onAddToPlan?: (carrier: OutboundPlanCarrier) => void;
  planCarrierIds?: Set<string>;
  showCarriersPageLink?: boolean;
};

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export const ExistingCarriers = ({
  shipmentId,
  pageSize = DEFAULT_PAGE_SIZE,
  onSelectCarrier,
  onAddToPlan,
  planCarrierIds,
  showCarriersPageLink,
}: ExistingCarriersProps) => {
  const [selectedCarrier, setSelectedCarrier] =
    useState<ExistingCarrierNode | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  const searchVariable = debouncedSearch.trim() || undefined;

  const {
    data,
    loading: carriersLoading,
    error,
    fetchMore,
  } = useGetExistingCarriersQuery({
    variables: {
      input: { shipmentId, search: searchVariable },
      first: pageSize,
    },
  });

  const carriers = data?.getExistingCarriers?.nodes || [];
  const pageInfo = data?.getExistingCarriers?.pageInfo;
  const totalCount = data?.getExistingCarriers?.totalCount ?? 0;

  const handleViewContacts = (carrier: ExistingCarrierNode) => {
    if (onSelectCarrier) {
      onSelectCarrier(carrier);
    } else {
      setSelectedCarrier(carrier);
    }
  };

  const handleLoadMore = () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;
    fetchMore({
      variables: { after: pageInfo.endCursor, first: pageSize },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const mergedNodes = [
          ...(prev.getExistingCarriers.nodes ?? []),
          ...(fetchMoreResult.getExistingCarriers.nodes ?? []),
        ];
        return {
          getExistingCarriers: {
            ...fetchMoreResult.getExistingCarriers,
            nodes: mergedNodes,
            edges: [
              ...(prev.getExistingCarriers.edges ?? []),
              ...(fetchMoreResult.getExistingCarriers.edges ?? []),
            ],
            count: mergedNodes.length,
          },
        };
      },
    });
  };

  if (error) {
    return (
      <div className="p-4 text-center">
        <Text className="text-red-500">
          Error loading carriers: {error.message}
        </Text>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <SearchField
          placeholder="Search by carrier name, MC #, or DOT #..."
          value={searchInput}
          onChange={setSearchInput}
          hasClearButton
          size="small"
          aria-label="Search carriers"
        />
        <div className="flex items-center justify-between">
          <Text type="body-sm" className="text-gray-700">
            Carriers ({totalCount})
          </Text>
        </div>
        <div className="flex h-[250px] flex-col gap-2 overflow-y-scroll">
          {carriersLoading && carriers.length === 0 ? (
            <div className="p-4 text-center">
              <Text className="text-gray-500">
                {searchInput.trim()
                  ? 'Searching carriers...'
                  : 'Loading carriers...'}
              </Text>
            </div>
          ) : carriers.length === 0 ? (
            <div className="p-4 text-center">
              <Text className="text-gray-500">
                {searchInput.trim()
                  ? 'No carriers match your search.'
                  : 'No carriers available. Please configure carrier integrations.'}
              </Text>
            </div>
          ) : (
            carriers.map((carrier) => (
              <div
                key={carrier.id}
                className="flex flex-row items-center justify-between gap-2 rounded-md bg-gray-100 p-2"
              >
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center gap-2">
                    <PiTruck />
                    <Text
                      isHeavy
                      type="body-xs"
                      className="line-clamp-1 overflow-hidden text-gray-800"
                    >
                      {carrier.name}
                    </Text>
                  </div>
                  <Text type="body-xs">MC #{carrier.mcNumber}</Text>
                </div>
                <div className="flex flex-row gap-1">
                  {onAddToPlan && (
                    <Button
                      variant={
                        planCarrierIds?.has(carrier.id)
                          ? 'tertiary'
                          : 'secondary'
                      }
                      size="xs"
                      isDisabled={planCarrierIds?.has(carrier.id)}
                      labelClassName={
                        planCarrierIds?.has(carrier.id) ? '!px-2' : undefined
                      }
                      icon={
                        planCarrierIds?.has(carrier.id) ? (
                          <PiCheck className="h-3 w-3" />
                        ) : (
                          <PiPlus className="h-3 w-3" />
                        )
                      }
                      onPress={() =>
                        onAddToPlan({
                          carrierId: carrier.id,
                          carrierName: carrier.name,
                          mcNumber: carrier.mcNumber,
                          contacts: carrier.contacts?.map((c) => ({
                            id: c.id,
                            name: c.name,
                            email: c.email,
                            phone: c.phone,
                          })),
                        })
                      }
                    >
                      {planCarrierIds?.has(carrier.id)
                        ? 'Added'
                        : 'Add to Plan'}
                    </Button>
                  )}
                  {carrier.contacts?.length > 0 && (
                    <Button
                      variant="secondary"
                      size="xs"
                      onPress={() => handleViewContacts(carrier)}
                    >
                      View Contacts
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
          {pageInfo?.hasNextPage && (
            <Button
              variant="secondary"
              size="xs"
              onPress={handleLoadMore}
              isDisabled={carriersLoading}
              className="mt-1 self-center"
            >
              {carriersLoading ? 'Loading...' : 'Load more'}
            </Button>
          )}
        </div>
        {showCarriersPageLink && (
          <div className="mt-1 text-center">
            <Link href="/carriers" size="small" className="text-brand-500">
              View all carriers on the Carriers page
            </Link>
          </div>
        )}
      </div>

      {!onSelectCarrier && selectedCarrier && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setSelectedCarrier(null)}
        >
          <div
            className="mx-4 w-full max-w-md rounded-lg bg-white p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <CarrierContactsList
              carrier={selectedCarrier}
              onBack={() => setSelectedCarrier(null)}
              onAddToPlan={onAddToPlan}
              isAddedToPlan={planCarrierIds?.has(selectedCarrier.id)}
            />
          </div>
        </div>
      )}
    </>
  );
};
